import { useState } from 'react';
import { Plus, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { useDesignations, useCreateDesignation, useDeleteDesignation } from '@/api/queries/employee.queries';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';

export default function DesignationsSettingsPage() {
  const { data, isLoading } = useDesignations();
  const { mutate: create, isPending: creating } = useCreateDesignation();
  const { mutate: remove } = useDeleteDesignation();

  const designations = data?.data || [];
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');

  const handleAdd = () => {
    if (!newName.trim()) return;
    create({ name: newName.trim() }, {
      onSuccess: () => { toast.success('Designation added'); setNewName(''); setShowAdd(false); },
      onError: () => toast.error('Failed to add'),
    });
  };

  const handleDelete = (id) => {
    remove(id, {
      onSuccess: () => toast.success('Designation removed'),
      onError: () => toast.error('Cannot delete — designation is in use'),
    });
  };

  return (
    <div className="space-y-5 fade-in max-w-xl">
      <PageHeader
        title="Designations"
        subtitle={`${designations.length} designations`}
        breadcrumb={[{ label: 'Settings', href: '/settings' }, { label: 'Designations' }]}
        actions={<Button size="sm" icon={Plus} onClick={() => setShowAdd(true)}>Add Designation</Button>}
      />

      {showAdd && (
        <div className="card flex gap-2">
          <input autoFocus value={newName} onChange={e => setNewName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
            placeholder="e.g. Senior Sales Manager"
            className="flex-1 h-10 px-3 bg-[var(--bg-muted)] border border-[var(--border)] rounded-[10px] text-[14px] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"/>
          <Button size="sm" onClick={handleAdd} disabled={creating}>{creating ? 'Adding…' : 'Add'}</Button>
          <button onClick={() => setShowAdd(false)} className="p-2 rounded-[8px] hover:bg-[var(--bg-muted)] text-[var(--text-secondary)]"><X size={15}/></button>
        </div>
      )}

      <div className="card p-0 overflow-hidden divide-y divide-[var(--border)]">
        {isLoading && <div className="px-5 py-8 text-center text-[13px] text-[var(--text-tertiary)]">Loading…</div>}
        {!isLoading && designations.length === 0 && (
          <div className="px-5 py-8 text-center text-[13px] text-[var(--text-tertiary)]">No designations yet. Add one above.</div>
        )}
        {designations.map(d => (
          <div key={d.id} className="flex items-center justify-between px-5 py-3.5 hover:bg-[var(--bg-muted)]">
            <p className="text-[14px] font-semibold text-[var(--text-primary)]">{d.name}</p>
            <button onClick={() => handleDelete(d.id)}
              className="p-2 rounded-[6px] hover:bg-[var(--danger)]/10 text-[var(--text-tertiary)] hover:text-[var(--danger)] transition-colors">
              <Trash2 size={14}/>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
